import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getApiUrl } from '../config';
import { useAuth } from './AuthContext';

type DeletedRecipe = {
  recipeId: number;
  recipeName: string;
  reopenOnUndo: boolean;
  key: number;
};

type RecipeUndoContextValue = {
  showRecipeUndo: (recipe: Omit<DeletedRecipe, 'key'>) => void;
};

const RecipeUndoContext = createContext<RecipeUndoContextValue | null>(null);
const UNDO_DURATION_MS = 10_000;

export const RecipeUndoProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [deletedRecipe, setDeletedRecipe] = useState<DeletedRecipe | null>(null);
  const [restoreError, setRestoreError] = useState<string | null>(null);
  const [restoring, setRestoring] = useState(false);
  const nextKeyRef = useRef(0);

  const showRecipeUndo = useCallback((recipe: Omit<DeletedRecipe, 'key'>) => {
    setRestoreError(null);
    setRestoring(false);
    setDeletedRecipe({ ...recipe, key: ++nextKeyRef.current });
  }, []);

  useEffect(() => {
    if (!deletedRecipe) return;
    const timeout = window.setTimeout(() => setDeletedRecipe(null), UNDO_DURATION_MS);
    return () => window.clearTimeout(timeout);
  }, [deletedRecipe]);

  const undo = async () => {
    if (!deletedRecipe || !token || restoring) return;
    setRestoring(true);
    setRestoreError(null);
    try {
      const response = await fetch(getApiUrl(`recipes/${deletedRecipe.recipeId}/restore`), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to restore recipe');

      document.dispatchEvent(new CustomEvent('recipeRestored', {
        detail: { recipeId: deletedRecipe.recipeId },
      }));
      if (deletedRecipe.reopenOnUndo) {
        navigate(`/recipes/${deletedRecipe.recipeId}/edit`);
      }
      setDeletedRecipe(null);
    } catch (error) {
      setRestoreError(error instanceof Error ? error.message : 'Failed to restore recipe');
      setRestoring(false);
    }
  };

  return (
    <RecipeUndoContext.Provider value={{ showRecipeUndo }}>
      {children}
      {deletedRecipe && (
        <div
          role="status"
          className="fixed bottom-4 right-4 z-[9999] flex w-[300px] flex-col rounded-lg bg-gray-800 p-3 text-white shadow-xl"
        >
          <style>{`@keyframes recipe-undo-countdown { from { transform: scaleX(1); } to { transform: scaleX(0); } }`}</style>
          <div className="mb-2 flex items-center justify-between">
            <div className="font-medium">Recipe deleted</div>
            <button
              type="button"
              className="text-gray-400 hover:text-white"
              onClick={() => setDeletedRecipe(null)}
              aria-label="Dismiss deletion notification"
            >
              ✕
            </button>
          </div>
          <div className="mb-3 text-sm">
            {restoreError || `${deletedRecipe.recipeName} can be restored for 10 seconds.`}
          </div>
          {!restoreError && (
            <div className="mb-3 h-1 w-full overflow-hidden rounded-full bg-gray-700">
              <div
                key={deletedRecipe.key}
                className="h-full origin-left bg-blue-500"
                style={{ animation: `recipe-undo-countdown ${UNDO_DURATION_MS}ms linear forwards` }}
              />
            </div>
          )}
          <button
            type="button"
            onClick={() => void undo()}
            disabled={restoring}
            className="self-end rounded bg-blue-500 px-4 py-1 text-sm font-medium text-white transition-colors hover:bg-blue-600 disabled:cursor-wait disabled:bg-blue-400"
          >
            {restoring ? 'Restoring…' : 'Undo'}
          </button>
        </div>
      )}
    </RecipeUndoContext.Provider>
  );
};

export const useRecipeUndo = (): RecipeUndoContextValue => {
  const context = useContext(RecipeUndoContext);
  if (!context) throw new Error('useRecipeUndo must be used inside RecipeUndoProvider');
  return context;
};
